import { captureRemixErrorBoundaryError } from '@sentry/remix';
import tailwind from '~/styles/tailwind.css?url';
import globalStyle from '~/styles/global.css?url';
import emblaStyle from '~/styles/embla.css?url';

import type {
    LinksFunction,
    MetaFunction,
    LoaderFunctionArgs,
} from '@remix-run/node';
import { json } from '@remix-run/node';
import {
    Links,
    Meta,
    Outlet,
    Scripts,
    ScrollRestoration,
    useLocation,
    useRouteError,
} from '@remix-run/react';
import { useEffect } from 'react';
import posthog from 'posthog-js';
import { generateMeta } from './services/meta';

export const meta: MetaFunction = () => {
    return generateMeta({});
};

export const links: LinksFunction = () => [
    { rel: 'stylesheet', href: tailwind },
    {
        rel: 'stylesheet',
        href: '/antd.css',
    },
    { rel: 'stylesheet', href: globalStyle },
    { rel: 'stylesheet', href: emblaStyle },
];

function ExternalScripts() {
    const externalScripts = [
        {
            name: 'telegram',
            src: 'https://telegram.org/js/telegram-web-app.js?56',
        },
        {
            name: 'cloudflare',
            src: 'https://static.cloudflareinsights.com/beacon.min.js',
            'data-cf-beacon': '{"token": "07a5e68a31ae4f9e85117538906b52b0"}',
        },
        {
            name: 'facebook',
            script: `!function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');window.fbq('init', '1126702481975362');`,
        },
        {
            name: 'google-tags',
            src: 'https://www.googletagmanager.com/gtag/js?id=AW-16850919995',
        },
    ];
    return (
        <>
            {externalScripts.map((scriptItem: any) => {
                const { name, src, script, ...otherProps } = scriptItem;
                return (
                    <script
                        {...otherProps}
                        key={`script-${name}`}
                        src={src || undefined}
                        dangerouslySetInnerHTML={{
                            __html: script || '',
                        }}
                    />
                );
            })}
            <noscript>
                <img
                    alt=''
                    height='1'
                    width='1'
                    style={{ display: 'none' }}
                    src='https://www.facebook.com/tr?id=1126702481975362&ev=PageView&noscript=1'
                />
            </noscript>
        </>
    );
}

export const ErrorBoundary = () => {
    const error = useRouteError();
    captureRemixErrorBoundaryError(error);
    console.error(error);
    return <div>Something went wrong</div>;
};

export default function App() {
    const location = useLocation();
    useEffect(() => {
        posthog.capture('$pageview');

        if (typeof window !== 'undefined' && window?.fbq) {
            window.fbq('track', 'PageView');
        }

        window.dataLayer = window.dataLayer || [];
        function gtag() {
            dataLayer.push(arguments);
        }
        gtag('js', new Date());
        gtag('config', 'AW-16850919995', {
            page_path: location.pathname,
            page_search: location.search,
        });
        gtag('event', 'page_view', {
            page_path: location.pathname + location.search,
        });
    }, [location]);

    return (
        <html lang='en' className='h-full w-full'>
            <head>
                <meta charSet='utf-8' />
                <meta
                    name='viewport'
                    content='width=device-width,initial-scale=1'
                />
                <link rel='icon' type='image/x-icon' href='/assets/logo.png' />
                <link
                    rel='icon'
                    type='image/png'
                    href='/favicon-96x96.png'
                    sizes='96x96'
                />
                <link rel='icon' type='image/svg+xml' href='/favicon.svg' />
                <link rel='shortcut icon' href='/favicon.ico' />
                <link
                    rel='apple-touch-icon'
                    sizes='180x180'
                    href='/apple-touch-icon.png'
                />
                <meta
                    name='apple-mobile-web-app-title'
                    content='AI Perfect Assistant'
                />
                <link rel='manifest' href='/app.webmanifest' />
                <link rel='apple-touch-icon' href='/assets/512.png' />
                <link
                    rel='canonical'
                    href={`https://perfectassistant.ai${location.pathname}`}
                />
                <meta
                    property='og:url'
                    content={`https://perfectassistant.ai${location.pathname}`}></meta>
                <Meta />
                <Links />
            </head>
            <body className='h-full w-full bg-slate-50'>
                <Outlet />
                <ScrollRestoration />
                <Scripts />
                <ExternalScripts />
            </body>
        </html>
    );
}
